<script>
export default {
  name: 'AiSettingsTabManager',
  props: {
    tabs: { type: Array, required: true },
    activeTab: { type: String, required: true },
  },
  emits: ['update:activeTab'],
  data() {
    return {
      expandedParents: {},
      showMobileTabSelector: false
    }
  },
  watch: {
    activeTab: {
      immediate: true,
      handler(newTab) {
        this.expandParentsForActiveTab(newTab)
      }
    },
    tabs: {
      immediate: true,
      handler() {
        this.cleanupExpandedParents()
        this.expandParentsForActiveTab(this.activeTab)
      },
      deep: true,
    }
  },
  mounted() {
    // Ensure the correct parent is expanded initially
    this.expandParentsForActiveTab(this.activeTab)
    // Add click outside listener for mobile dropdown
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    onSelect(key, { closeDropdown = true } = {}) {
      if (closeDropdown) {
        this.showMobileTabSelector = false
      }
      this.$emit('update:activeTab', key)
    },
    isTabActive(tab) {
      if (this.isParentTab(tab)) {
        return (
          this.activeTab === tab.key ||
          (tab.subTabs || []).some(subTab => subTab.key === this.activeTab)
        )
      }
      return this.activeTab === tab.key
    },
    isSubTabActive(key) {
      return this.activeTab === key
    },
    toggleMobileTabSelector() {
      this.showMobileTabSelector = !this.showMobileTabSelector;
    },
    getCurrentTabName() {
      const allTabs = [...this.tabs];
      // Add sub-tabs to the search
      this.tabs.forEach(tab => {
        if (tab.subTabs) {
          allTabs.push(...tab.subTabs);
        }
      });
      const currentTab = allTabs.find(tab => tab.key === this.activeTab);
      return currentTab ? currentTab.name : 'Select Tab';
    },
    getTabIcon(key) {
      const iconMap = {
        'overview': 'chart-line',
        'business-data': 'briefcase',
        'business-data:ai-rules': 'text-bullet-list-square',
        'business-data:ai-sample-sentences': 'comment-multiple',
        'business-data:file-sources': 'document-arrow-down',
        'business-data:faqs': 'message-circle-question',
        'business-data:data-items': 'database',
        'business-data:image-library': 'images',
        'business-data:web-sources': 'globe',
        'data-collection': 'folder-input',
        'summary': 'file-text'
      };
      return iconMap[key] || 'circle';
    },
    handleClickOutside(event) {
      // Close dropdown if clicking outside
      if (this.showMobileTabSelector && !this.$el.contains(event.target)) {
        this.showMobileTabSelector = false;
      }
    },
    isParentTab(tab) {
      return Array.isArray(tab.subTabs) && tab.subTabs.length > 0
    },
    isParentExpanded(key) {
      return Boolean(this.expandedParents[key])
    },
    setParentExpanded(key, value) {
      this.expandedParents = { ...this.expandedParents, [key]: value }
    },
    handleParentSelect(tab) {
      const willExpand = !this.isParentExpanded(tab.key)
      if (!willExpand && tab.allowParentView === false && this.parentContainsActive(tab, this.activeTab)) {
        // Prevent collapsing when we have no standalone view to show
        return
      }
      this.setParentExpanded(tab.key, willExpand)
      if (willExpand) {
        const target = tab.defaultActiveKey || tab.subTabs?.[0]?.key || tab.key
        if (target) {
          this.onSelect(target, { closeDropdown: false })
        }
      } else if (this.parentContainsActive(tab, this.activeTab) && tab.allowParentView !== false) {
        this.onSelect(tab.key, { closeDropdown: false })
      }
    },
    parentContainsActive(tab, key) {
      if (!this.isParentTab(tab)) {
        return false
      }
      if (tab.key === key) {
        return true
      }
      return tab.subTabs.some(sub => sub.key === key)
    },
    expandParentsForActiveTab(activeKey) {
      if (!activeKey) {
        return
      }
      this.tabs.forEach(tab => {
        if (this.isParentTab(tab) && this.parentContainsActive(tab, activeKey)) {
          this.setParentExpanded(tab.key, true)
        }
      })
    },
    cleanupExpandedParents() {
      const parentKeys = this.tabs.filter(tab => this.isParentTab(tab)).map(tab => tab.key)
      const nextState = {}
      parentKeys.forEach(key => {
        if (this.expandedParents[key]) {
          nextState[key] = true
        }
      })
      this.expandedParents = nextState
    }
  },
};
</script>

<template>
  <aside class="w-full h-full" aria-label="AI Settings Navigation">
    <!-- Mobile: Dropdown selector -->
    <div class="sm:hidden relative z-50">
      <button
        @click="toggleMobileTabSelector"
        class="w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200"
        :class="[
          activeTab 
            ? 'bg-hilab text-white border border-hilab' 
            : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
        ]"
      >
        <span 
          class="font-medium"
          :class="activeTab ? 'text-white' : 'text-slate-900 dark:text-slate-100'"
        >
          {{ getCurrentTabName() }}
        </span>
        <fluent-icon 
          :icon="showMobileTabSelector ? 'chevron-up' : 'chevron-down'" 
          size="16" 
          class="transition-transform duration-200"
          :class="[
            { 'rotate-180': showMobileTabSelector },
            activeTab ? 'text-white/80' : 'text-slate-500'
          ]"
        />
      </button>

      <!-- Dropdown menu -->
      <div 
        v-if="showMobileTabSelector"
        class="absolute top-full left-3 right-3 mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg z-[60] max-h-80 overflow-y-auto"
      >
        <div class="py-2">
          <template v-for="tab in tabs" :key="tab.key">
            <div v-if="isParentTab(tab)">
              <button
                :class="[
                  'w-full text-left px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/50',
                  isTabActive(tab)
                    ? 'text-white bg-hilab'
                    : 'text-slate-700 dark:text-slate-300'
                ]"
                @click="handleParentSelect(tab)"
              >
                <span>{{ tab.name }}</span>
                <fluent-icon 
                  :icon="isParentExpanded(tab.key) ? 'chevron-up' : 'chevron-down'" 
                  size="14" 
                  class="text-slate-400 transition-transform duration-200"
                />
              </button>

              <div v-if="isParentExpanded(tab.key)" class="bg-slate-50/50 dark:bg-slate-800/50">
                <button
                  v-for="subTab in tab.subTabs"
                  :key="subTab.key"
                  :class="[
                    'w-full text-left px-8 py-2 text-sm font-medium transition-colors hover:bg-slate-100 dark:hover:bg-slate-700/70',
                    isSubTabActive(subTab.key)
                      ? 'text-white bg-hilab'
                      : 'text-slate-600 dark:text-slate-400'
                  ]"
                  @click="onSelect(subTab.key)"
                >
                  <span>{{ subTab.name }}</span>
                </button>
              </div>
            </div>

            <button
              v-else
              :class="[
                'w-full text-left px-4 py-2.5 text-sm font-medium transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50',
                isTabActive(tab)
                  ? 'text-white bg-hilab'
                  : 'text-slate-700 dark:text-slate-300'
              ]"
              @click="onSelect(tab.key)"
            >
              <span>{{ tab.name }}</span>
            </button>
          </template>
        </div>
      </div>
    </div>

    <!-- Tablet+: Vertical sidebar (original layout) -->
    <div class="hidden sm:block h-full p-4">
      <nav class="flex flex-col gap-2" aria-label="AI Settings Tabs">
        <template v-for="tab in tabs" :key="tab.key">
          <div v-if="isParentTab(tab)">
            <button
              :class="[
                'w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-between',
                isTabActive(tab)
                  ? 'text-white bg-hilab'
                  : 'text-slate-600 hover:text-slate-900 dark:text-slate-300/80 dark:hover:text-slate-100'
              ]"
              @click="handleParentSelect(tab)"
            >
              <span>{{ tab.name }}</span>
              <fluent-icon 
                :icon="isParentExpanded(tab.key) ? 'chevron-up' : 'chevron-down'" 
                size="14" 
                class="transition-transform duration-200"
              />
            </button>

            <div v-if="isParentExpanded(tab.key)" class="ml-3 mt-2 space-y-1">
              <button
                v-for="subTab in tab.subTabs"
                :key="subTab.key"
                :class="[
                  'w-full text-left px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-2',
                  isSubTabActive(subTab.key)
                    ? 'text-white bg-hilab'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                ]"
                @click="onSelect(subTab.key)"
              >
                <span>{{ subTab.name }}</span>
              </button>
            </div>
          </div>

          <button
            v-else
            :class="[
              'w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors',
              isTabActive(tab)
                ? 'text-white bg-hilab'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-300/80 dark:hover:text-slate-100'
            ]"
            @click="onSelect(tab.key)"
          >
            {{ tab.name }}
          </button>
        </template>
      </nav>
    </div>
  </aside>
</template>
